import { isAbortOrCanceledError } from "@mirage-web/utils/abort-signals";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { useRouter, Link, Navigate, useMatches, ToPathOption } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog";
import {
  isMissingOasisPermissionError,
  isMissingSessionError,
  isNotFoundError,
} from "~/lib/network/errors";

interface ErrorComponentProps {
  error: Error;
}

export function NotFoundComponent() {
  return (
    <div>
      <div className="relative flex flex-col items-center justify-center gap-2 h-dvh w-dvw pointer-events-none">
        <h1 className="text-4xl font-bold">oasis</h1>
        <p className="text-muted-foreground">the page you are looking for does not exist.</p>
        <Link to="/home">
          <Button variant="default">go home</Button>
        </Link>
      </div>
    </div>
  );
}

export function DefaultErrorComponent(props: ErrorComponentProps) {
  const queryErrorResetBoundary = useQueryErrorResetBoundary();
  const isAnInstanceOfAbortOrCanceledError = isAbortOrCanceledError(props.error);
  const [pathname] = useState(() => window.location.pathname);

  const previousMatchedPath = useMatches({
    select(m) {
      return m[m.length - 2]?.fullPath;
    },
  });

  useEffect(() => {
    if (isAnInstanceOfAbortOrCanceledError) {
      queryErrorResetBoundary.reset();
    }
  }, [isAnInstanceOfAbortOrCanceledError, queryErrorResetBoundary]);

  if (isAnInstanceOfAbortOrCanceledError) {
    return null;
  }

  if (isMissingSessionError(props.error)) {
    return <Navigate to="/session" search={{ redirectTo: pathname }} />;
  }

  const errorDialogProps = getErrorDialogProps(props.error, previousMatchedPath);

  return <ErrorDialog {...errorDialogProps} />;
}

// copied from: https://tanstack.com/router/latest/docs/framework/react/guide/external-data-loading#error-handling-with-tanstack-query
export function SuspenseErrorComponent(props: ErrorComponentProps) {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();
  const isAnInstanceOfAbortOrCanceledError = isAbortOrCanceledError(props.error);

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  useEffect(() => {
    if (isAbortOrCanceledError(props.error)) {
      queryErrorResetBoundary.reset();
    }
  }, [props.error, queryErrorResetBoundary]);

  if (isAnInstanceOfAbortOrCanceledError) {
    return null;
  }

  return (
    <ErrorDialog
      description="Something went wrong. Please try again."
      errorMessage={props.error.message}
      cta={
        <Button
          variant="default"
          onClick={() => {
            router.invalidate();
          }}
        >
          Retry
        </Button>
      }
    />
  );
}

function ErrorDialog({
  title,
  description,
  errorMessage,
  cta,
}: {
  title?: string;
  description?: string;
  errorMessage?: string;
  cta: React.ReactNode;
}) {
  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        className="block max-h-[85vh] overflow-y-auto sm:max-w-sm"
      >
        <DialogTitle className="text-lg font-semibold text-destructive mb-4">
          {title || "Error"}
        </DialogTitle>
        {description && (
          <DialogDescription className="text-sm text-muted-foreground mb-4">
            {description}
          </DialogDescription>
        )}
        {errorMessage && (
          <pre className="p-2 rounded bg-muted text-xs text-foreground whitespace-pre-wrap overflow-auto max-h-32 mb-6">
            {errorMessage}
          </pre>
        )}
        <div className="flex justify-end">{cta}</div>
      </DialogContent>
    </Dialog>
  );
}

function getErrorDialogProps(error: Error, previousMatchedPath?: ToPathOption) {
  const isNotFound = isNotFoundError(error);

  if (isNotFound) {
    return {
      title: "Not Found",
      errorMessage: error.message,
      cta: (
        <Link to={previousMatchedPath ?? "/home"}>
          <Button variant="default">go {previousMatchedPath ? "back" : "home"}</Button>
        </Link>
      ),
    };
  }

  const [isPermissionError] = isMissingOasisPermissionError(error);

  if (isPermissionError) {
    return {
      title: "Permission Denied",
      errorMessage: error.message,
      cta: (
        <Link to="/home">
          <Button variant="default">go home</Button>
        </Link>
      ),
    };
  }

  return {
    title: "Error",
    description: "Something went wrong. Please try again.",
    cta: (
      <Button variant="default" onClick={() => window.location.reload()}>
        retry
      </Button>
    ),
  };
}
